import { Button, Input, Label } from '@betstarters/ui'
import { useTranslation } from 'react-i18next'
import { ErrorBanner } from './catalog'
import { Select } from './controls'
import type { GameType } from '../pages/GameTypes'

/**
 * The create/edit forms of the Games family, shared by the list dialogs and the
 * detail page — one definition of the fields, their limits and their hints, so
 * a new column lands in both places at once.
 */

// ---- game type -------------------------------------------------------------

// The two hub flags are NOT edited here (the backoffice does not show them),
// but they stay in the value so a save carries the row's existing settings back
// unchanged. Dropping them from the payload would silently clear columns other
// services read.
export interface GameTypeFormValue {
  gameTypeId: string
  gameType: string
  hasGameHubStructure: boolean
  hasSportsbookHubStructure: boolean
}

export const emptyGameTypeForm: GameTypeFormValue = {
  gameTypeId: '',
  gameType: '',
  hasGameHubStructure: false,
  hasSportsbookHubStructure: false,
}

export function isGameTypeFormValid(v: GameTypeFormValue, isCreate: boolean): boolean {
  return v.gameType.trim() !== '' && (!isCreate || v.gameTypeId !== '')
}

export function GameTypeForm({
  value,
  onChange,
  onSubmit,
  onCancel,
  isCreate,
  pending,
  error,
  submitLabel,
}: {
  value: GameTypeFormValue
  onChange: (v: GameTypeFormValue) => void
  onSubmit: () => void
  onCancel: () => void
  /** On create the caller picks the id; on edit it is immutable. */
  isCreate: boolean
  pending: boolean
  error: string
  submitLabel: string
}) {
  const { t } = useTranslation('app')
  const set = (patch: Partial<GameTypeFormValue>) => onChange({ ...value, ...patch })

  return (
    <form
      className="flex max-w-xl flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault()
        if (isGameTypeFormValid(value, isCreate)) onSubmit()
      }}
    >
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="game-type-id">{t('gameTypes.columns.id')}</Label>
        <Input
          id="game-type-id"
          value={value.gameTypeId}
          onChange={(e) => set({ gameTypeId: e.target.value.replace(/[^0-9]/g, '') })}
          disabled={!isCreate}
          placeholder="10"
          autoFocus={isCreate}
        />
        <p className="text-xs text-muted-foreground">{t('gameTypes.idHint')}</p>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="game-type-name">{t('gameTypes.columns.name')}</Label>
        <Input
          id="game-type-name"
          value={value.gameType}
          onChange={(e) => set({ gameType: e.target.value })}
          maxLength={20}
          placeholder="CASINO"
          autoFocus={!isCreate}
        />
        <p className="text-xs text-muted-foreground">{t('gameTypes.nameHint')}</p>
      </div>

      <ErrorBanner message={error} />

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" disabled={pending || !isGameTypeFormValid(value, isCreate)}>
          {submitLabel}
        </Button>
      </div>
    </form>
  )
}

// ---- game subtype ----------------------------------------------------------

export interface GameSubTypeFormValue {
  gameTypeId: string
  gameSubType: string
  icon: string
}

export const emptyGameSubTypeForm: GameSubTypeFormValue = {
  gameTypeId: '',
  gameSubType: '',
  icon: '',
}

export function isGameSubTypeFormValid(v: GameSubTypeFormValue): boolean {
  return v.gameSubType.trim() !== '' && v.gameTypeId !== ''
}

export function GameSubTypeForm({
  value,
  onChange,
  onSubmit,
  onCancel,
  gameTypes,
  lockParent = false,
  pending,
  error,
  submitLabel,
}: {
  value: GameSubTypeFormValue
  onChange: (v: GameSubTypeFormValue) => void
  onSubmit: () => void
  onCancel: () => void
  gameTypes: GameType[]
  /** Hides the parent picker when the form is already scoped to one type. */
  lockParent?: boolean
  pending: boolean
  error: string
  submitLabel: string
}) {
  const { t } = useTranslation('app')
  const set = (patch: Partial<GameSubTypeFormValue>) => onChange({ ...value, ...patch })

  return (
    <form
      className="flex max-w-xl flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault()
        if (isGameSubTypeFormValid(value)) onSubmit()
      }}
    >
      {!lockParent && (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="subtype-parent">{t('gameSubTypes.columns.gameType')}</Label>
          <Select
            value={value.gameTypeId}
            onValueChange={(v) => set({ gameTypeId: v })}
            items={gameTypes.map((g) => ({
              value: String(g.gameTypeId),
              label: `${g.gameTypeId} — ${g.gameType}${
                g.isDisabledByPlatform ? ` (${t('common.status.disabled')})` : ''
              }`,
            }))}
            className="w-full"
          />
        </div>
      )}

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="subtype-name">{t('gameSubTypes.columns.name')}</Label>
        <Input
          id="subtype-name"
          value={value.gameSubType}
          onChange={(e) => set({ gameSubType: e.target.value })}
          maxLength={100}
          placeholder="Live Casino"
          autoFocus
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="subtype-icon">{t('gameSubTypes.columns.icon')}</Label>
        <Input
          id="subtype-icon"
          value={value.icon}
          onChange={(e) => set({ icon: e.target.value })}
          maxLength={1000}
          placeholder="/icons/live.svg"
        />
        <p className="text-xs text-muted-foreground">{t('gameSubTypes.iconHint')}</p>
      </div>

      <ErrorBanner message={error} />

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" disabled={pending || !isGameSubTypeFormValid(value)}>
          {submitLabel}
        </Button>
      </div>
    </form>
  )
}
